import * as tslib_1 from "tslib";
import React from 'react';
import { Manager, Reference } from 'react-popper';
import ReactDOM from 'react-dom';
import activeElement from 'dom-helpers/activeElement';
import contains from 'dom-helpers/query/contains';
import warning from 'warning';
import Menu from '../Menu/Menu';
import MenuItem from '../Menu/MenuItem';
import MenuWrapper from '../Menu/MenuWrapper';
import MenuItemGroup from '../Menu/MenuItemGroup';
import Toggle from './Toggle';
import { matchComponent, chainCallbacks } from './utils';
import { KEY } from '../types';
import { TooltipPopper } from '../Tooltip';
import { DropdownNativeContext } from './context';
var defaultState = {
    isOpen: false,
};
var Dropdown = /** @class */ (function (_super) {
    tslib_1.__extends(Dropdown, _super);
    function Dropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = tslib_1.__assign({}, defaultState, { isOpen: !!_this.props.isOpen, selectedValue: _this.props.selectedValue });
        // does any of the children have focus
        _this.focusInDropdown = false;
        // which event triggered Dropdown open?
        _this.lastOpenEventType = null;
        // helper methods to detect component types
        _this.isDropdownToggle = matchComponent(Toggle);
        _this.isDropdownMenu = matchComponent(Menu);
        _this.isDropdownMenuWrapper = matchComponent(MenuWrapper);
        _this.openMenu = function (isOpen) {
            _this.setState({ isOpen: isOpen });
            if (isOpen && _this.props.onOpen) {
                _this.props.onOpen();
            }
        };
        _this.handleToggleClick = function (event) {
            if (!_this.props.disabled) {
                _this.toggleDropdown('click');
            }
        };
        _this.handleKeyControls = function (event) {
            if (_this.props.disabled) {
                return;
            }
            switch (event.keyCode) {
                case KEY.ENTER:
                    if (!_this.state.isOpen) {
                        _this.toggleDropdown('keydown');
                    }
                    break;
                case KEY.DOWN:
                    if (!_this.state.isOpen) {
                        _this.toggleDropdown('keydown');
                    }
                    else if (_this.menu.focusNext) {
                        _this.menu.focusNext();
                    }
                    event.preventDefault();
                    break;
                case KEY.UP:
                    if (!_this.state.isOpen) {
                        _this.toggleDropdown('keyup');
                    }
                    else if (_this.menu && _this.menu.focusPrevious) {
                        _this.menu.focusPrevious();
                    }
                    event.preventDefault();
                    break;
                case KEY.ESCAPE:
                case KEY.TAB:
                    _this.closeDropdown();
                    break;
                default:
            }
        };
        _this.handleMouseEnter = function (event) {
            var openOnHover = _this.props.openOnHover;
            if (openOnHover) {
                _this.openMenu(true);
            }
        };
        _this.handleMouseLeave = function (event) {
            if (_this.props.openOnHover) {
                _this.openMenu(false);
            }
        };
        _this.handleOnSelect = function (selectedValue) {
            _this.setState({ selectedValue: selectedValue });
        };
        _this.closeDropdown = function () {
            if (_this.state.isOpen) {
                _this.toggleDropdown(null);
                return;
            }
            if (_this.props.openOnHover) {
                _this.openMenu(false);
            }
        };
        _this.toggleDropdown = function (eventType) {
            var shouldOpen = !_this.state.isOpen;
            if (shouldOpen) {
                _this.lastOpenEventType = eventType;
            }
            _this.openMenu(shouldOpen);
        };
        return _this;
    }
    Dropdown.prototype.render = function () {
        var _this = this;
        var _a = this.props, children = _a.children, componentClass = _a.componentClass, componentProps = _a.componentProps, componentType = _a.componentType, disabled = _a.disabled, onOpen = _a.onOpen, onClose = _a.onClose, onSelect = _a.onSelect, menuAlignment = _a.menuAlignment, usePortal = _a.usePortal, onMouseLeave = _a.onMouseLeave, onMouseEnter = _a.onMouseEnter, isNative = _a.native, initialIsOpen = _a.isOpen, openOnHover = _a.openOnHover, selectedValue = _a.selectedValue, props = tslib_1.__rest(_a, ["children", "componentClass", "componentProps", "componentType", "disabled", "onOpen", "onClose", "onSelect", "menuAlignment", "usePortal", "onMouseLeave", "onMouseEnter", "native", "isOpen", "openOnHover", "selectedValue"]);
        var isOpen = this.state.isOpen;
        // This is necessary to ensure the compiler that the componentClass will not be empty even though it's optional
        var Component = componentClass || Dropdown.defaultProps.componentClass;
        var dropdownContent = React.Children.map(children, function (child) {
            if (!React.isValidElement(child)) {
                return child;
            }
            if (_this.isDropdownToggle(child)) {
                return _this.renderToggle(child, {
                    disabled: disabled,
                    isOpen: isOpen,
                });
            }
            if (_this.isDropdownMenu(child) || _this.isDropdownMenuWrapper(child)) {
                if (!isNative && !isOpen) {
                    return;
                }
                return _this.renderMenu(child, {
                    selectedValue: _this.state.selectedValue,
                    onClose: onClose,
                    onSelect: onSelect,
                    menuAlignment: menuAlignment,
                    usePortal: usePortal,
                    isNative: isNative,
                });
            }
            return child;
        });
        if (!isNative) {
            // Only use popper for non native dropdowns
            dropdownContent = React.createElement(Manager, null, dropdownContent);
        }
        return (React.createElement(DropdownNativeContext.Provider, { value: !!isNative },
            React.createElement(Component, tslib_1.__assign({ className: this.props.className, onMouseEnter: chainCallbacks(onMouseEnter, this.handleMouseEnter), onMouseLeave: chainCallbacks(onMouseLeave, this.handleMouseLeave) }, props, componentProps), dropdownContent)));
    };
    Dropdown.prototype.renderToggle = function (child, props) {
        var _this = this;
        var innerRef = function (c) {
            _this.toggle = c;
        };
        if (typeof child.ref === 'string') {
            warning(false, 'String refs are not supported on `<Dropdown.Toggle>` components. ' +
                'To apply a ref to the component use the callback signature:\n\n ' +
                'https://facebook.github.io/react/docs/more-about-refs.html#the-ref-callback-attribute');
        }
        else {
            innerRef = chainCallbacks(child.ref, innerRef);
        }
        var clonedChild = React.cloneElement(child, tslib_1.__assign({}, props, { ref: innerRef, onClick: chainCallbacks(child.props.onClick, this.handleToggleClick), onKeyDown: chainCallbacks(child.props.onKeyDown, this.handleKeyControls) }));
        if (this.props.native) {
            // Do not use popper stuff for native dropdowns
            return clonedChild;
        }
        return (React.createElement(Reference, null, function (_a) {
            var ref = _a.ref;
            return React.createElement("div", { ref: ref }, clonedChild);
        }));
    };
    Dropdown.prototype.renderMenu = function (child, _a) {
        var _this = this;
        var onClose = _a.onClose, onSelect = _a.onSelect, selectedValue = _a.selectedValue, menuAlignment = _a.menuAlignment, usePortal = _a.usePortal, isNative = _a.isNative, props = tslib_1.__rest(_a, ["onClose", "onSelect", "selectedValue", "menuAlignment", "usePortal", "isNative"]);
        var innerRef = function (c) {
            _this.menu = c;
        };
        if (typeof child.ref === 'string') {
            warning(false, 'String refs are not supported on `<Dropdown.Menu>` components. ' +
                'To apply a ref to the component use the callback signature:\n\n ' +
                'https://facebook.github.io/react/docs/more-about-refs.html#the-ref-callback-attribute');
        }
        else {
            innerRef = chainCallbacks(child.ref, innerRef);
        }
        var menuContent = React.cloneElement(child, tslib_1.__assign({}, props, { ref: innerRef, onClose: chainCallbacks(child.props.onClose, onClose, this.closeDropdown), onSelect: chainCallbacks(child.props.onSelect, onSelect, this.handleOnSelect, this.closeDropdown) }));
        if (isNative) {
            // Do not use popper stuff for native dropdowns
            return menuContent;
        }
        return (React.createElement(TooltipPopper, { usePortal: usePortal, positionOnly: true, placement: menuAlignment === 'left' ? 'bottom-start' : 'bottom-end' }, menuContent));
    };
    Dropdown.prototype.componentDidMount = function () {
        this.focusOnOpen();
    };
    Dropdown.prototype.componentWillUpdate = function (nextProps, nextState) {
        if (!nextState.isOpen && this.state.isOpen) {
            this.focusInDropdown = false;
            if (this.menu !== null) {
                this.focusInDropdown = contains(
                // eslint-disable-next-line
                ReactDOM.findDOMNode(this.menu), activeElement(document));
            }
        }
    };
    Dropdown.prototype.componentDidUpdate = function (prevProps, prevState) {
        var isOpen = this.state.isOpen;
        var wasOpen = prevState.isOpen;
        if (isOpen && !wasOpen) {
            this.focusOnOpen();
        }
        if (!isOpen && wasOpen) {
            // if focus hasn't already moved from the menu lets return it to the toggle
            if (this.focusInDropdown) {
                this.focusInDropdown = false;
                this.focus();
            }
        }
    };
    Dropdown.prototype.focusOnOpen = function () {
        if (this.lastOpenEventType === 'keydown') {
            // menu && menu.focusNext && menu.focusNext();
            return;
        }
        if (this.lastOpenEventType === 'keyup') {
            // menu && menu.focusPrevious && menu.focusPrevious();
            return;
        }
    };
    Dropdown.prototype.focus = function () {
        // eslint-disable-next-line
        var toggle = ReactDOM.findDOMNode(this.toggle);
        if (toggle && toggle.focus) {
            toggle.focus();
        }
    };
    Dropdown.defaultProps = {
        componentClass: 'div',
        componentType: Dropdown,
        disabled: false,
        isOpen: false,
        openOnHover: false,
        menuAlignment: 'left',
        usePortal: false,
    };
    Dropdown.Menu = Menu;
    Dropdown.MenuItem = MenuItem;
    Dropdown.ItemGroup = MenuItemGroup;
    Dropdown.MenuWrapper = MenuWrapper;
    Dropdown.Toggle = Toggle;
    return Dropdown;
}(React.Component));
export default Dropdown;
//# sourceMappingURL=Dropdown.js.map